import { Component, EventEmitter, Inject, Input, OnInit, Output, Self } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DataForm } from '@libs/shared/static-data/Data-form.static';
import { UserVerificationService } from '@libs/services/user-verification.service';
import { ActivatedRoute } from '@angular/router';
import { UnsubscribeService } from '@libs/shared/services/unsubscribe.service';
import { VerificationActionType } from '@libs/enums/verification-action-type.enum';
import { switchMap, takeUntil } from 'rxjs/operators';
import { IDataLayer, IFormControls } from '@libs/models/models';
import { GroupType } from '@libs/types/group.type';
import { EnvironmentType } from '@libs/models/environment.type';
import { DATA_LAYER, ENVIRONMENT, SESSION_STORAGE } from '@libs/shared/injections/window';
import { environment } from '@libs/environment';
import { SendEventHelper } from '@libs/helpers/send-event.helper';
import { RecaptchaHelper } from '@libs/helpers/recaptcha.helper';
import { from } from 'rxjs';
import { VerificationType, VerificationTypeEnum } from '@libs/configs/component-configs/countdown.component-config';

@Component({
  selector: 'app-auth-phone',
  templateUrl: `./templates/${environment.group}/page.html`,
  styleUrls: ['./auth-phone.component.sass', `./templates/${environment.group}/style.sass`],
  providers: [UnsubscribeService],
})
export class AuthPhoneComponent implements OnInit {
  @Input()
  public production: boolean;

  @Input()
  public group: GroupType;

  @Input()
  public isCalculateEntry: boolean;

  @Output()
  public readonly emitData: EventEmitter<any> = new EventEmitter<any>();

  public verificationType: VerificationType = VerificationTypeEnum.PHONE;
  public loginForm: FormGroup;
  public isAuthConfirm: boolean = false;
  public isAuthCallConfirm: boolean = false;
  public isResend: boolean = false;
  public withCaptcha: boolean = false;
  public phone: string;
  public isDisabled: boolean = false;
  public smsViber: boolean = false;

  constructor(
    @Inject(DATA_LAYER) private dataLayer: IDataLayer[],
    @Inject(SESSION_STORAGE) private sessionStorage: Storage,
    @Inject(ENVIRONMENT) private environment: EnvironmentType,
    public verificationService: UserVerificationService,
    @Self() private destroyStream$: UnsubscribeService,
    public route: ActivatedRoute,
    private eventHelper: SendEventHelper,
    private recaptchaHelper: RecaptchaHelper,
  ) {}

  get f(): IFormControls {
    return this.loginForm.controls;
  }

  public ngOnInit(): void {
    this.activateForm();
  }

  public activateForm(): void {
    this.loginForm = DataForm.getLoginFormGroup();
  }

  public editPhone(event: boolean): void {
    if (event) {
      this.isResend = false;
      this.isAuthConfirm = false;
      this.isAuthCallConfirm = false;
    }
  }

  public verificationClient(): void {
    this.verificationService
      .sendSmsCode(this.phone, VerificationActionType.LOGIN, false, this.verificationType)
      .pipe(takeUntil(this.destroyStream$))
      .subscribe(
        () => {
          this.isDisabled = false;
          this.isResend = false;
          this.isAuthConfirm = true;
          this.emitDataToParent();
        },
        () => (this.isDisabled = false),
      );
    this.dataLayer.push({
      event: 'auth_funnel_phone_entered',
      text: 'Продовжити',
      funnel_step: 'Step 1',
      phone: this.phone,
    });
  }

  public verificationClientPhoneCall(): void {
    this.isAuthCallConfirm = true;
    this.isAuthConfirm = false;
    this.isResend = false;
    this.emitDataToParent();
  }

  public verificationClientPhoneCallWithCaptcha(): void {
    this.withCaptcha = true;
    this.isAuthCallConfirm = true;
    this.isAuthConfirm = false;
    this.isResend = false;
    this.emitDataToParent();
  }

  public verificationClientWithCaptcha(): void {
    from(this.recaptchaHelper.getRecaptchaToken('login'))
      .pipe(
        switchMap((reCaptchaToken: string) =>
          this.verificationService.sendSmsCodeWithCaptcha(
            this.phone,
            VerificationActionType.LOGIN,
            reCaptchaToken,
            false,
            this.verificationType,
          ),
        ),
        takeUntil(this.destroyStream$),
      )
      .subscribe(
        () => {
          this.isDisabled = false;
          this.isAuthConfirm = true;
          this.isResend = false;
          this.emitDataToParent();
        },
        () => (this.isDisabled = false),
      );
  }

  public timeGone(): void {
    if (this.isAuthCallConfirm) {
      this.isResend = true;
      this.isAuthCallConfirm = false;
      this.isAuthConfirm = false;
    }
    if (this.isAuthConfirm) {
      this.isResend = false;
      this.isAuthCallConfirm = false;
      this.isAuthConfirm = true;
    }
    this.emitDataToParent();
  }

  public chooseWayRepeat(type: VerificationType): void {
    this.verificationType = type;
    this.isResend = false;
    this.isAuthCallConfirm = false;
    this.isAuthConfirm = true;

    this.environment.initRecaptcha ? this.verificationClientWithCaptcha() : this.verificationClient();
  }

  public emitDataToParent(): void {
    this.emitData.emit({
      isAuthCallConfirm: this.isAuthCallConfirm,
      isAuthConfirm: this.isAuthConfirm,
      isResend: this.isResend,
      withCaptcha: this.withCaptcha,
      verificationType: this.verificationType,
      smsViber: this.smsViber,
      phone: this.phone,
    });
  }

  public onSubmitRikki(): void {
    this.loginForm.get('phone').markAsTouched();
    this.eventHelper.send('clicked button of ringing from 1', this.loginForm.value.phone);
    if (this.loginForm.invalid) {
      this.dataLayer.push({
        event: 'auth_error',
        id: 'phone',
        text: 'Невірний формат телефону',
      });
    } else {
      this.isDisabled = true;
      this.phone = this.loginForm.value.phone;
      this.sessionStorage.setItem('phone', this.phone);
      this.environment.initRecaptcha ? this.verificationClientPhoneCallWithCaptcha() : this.verificationClientPhoneCall();
    }
  }

  public onSubmit(): void {
    this.loginForm.get('phone').markAsTouched();
    if (this.loginForm.invalid) {
      return;
    }
    this.isDisabled = true;
    this.phone = this.loginForm.value.phone;
    this.sessionStorage.setItem('phone', this.phone);
    this.environment.initRecaptcha ? this.verificationClientWithCaptcha() : this.verificationClient();
  }
}
